/**
 * FRONT-3228 — the auto-compact threshold stops, as a pure module. Claude Code
 * compacts its conversation at a percentage of its context; the person picks
 * one of 50…90 in steps of 5, or "Default" (Claude Code's own, nothing sent).
 * The slider is a row of positions; position 0..8 are the percentages and
 * position 9 is Default, which maps to `null` on the wire.
 */
export const AUTO_COMPACT_PERCENTS = [50, 55, 60, 65, 70, 75, 80, 85, 90] as const;

/** Slider positions: one per percentage, then Default last. */
export const AUTO_COMPACT_DEFAULT_POSITION = AUTO_COMPACT_PERCENTS.length;
export const AUTO_COMPACT_MAX_POSITION = AUTO_COMPACT_DEFAULT_POSITION;

/** The slider position of a saved value; an unknown or null value is Default. */
export function autoCompactPosition(pct: number | null | undefined): number {
  const i = pct === null || pct === undefined ? -1 : AUTO_COMPACT_PERCENTS.indexOf(pct as (typeof AUTO_COMPACT_PERCENTS)[number]);
  return i === -1 ? AUTO_COMPACT_DEFAULT_POSITION : i;
}

/** The value a slider position stands for (null = Default); out-of-range positions read as Default. */
export function autoCompactValue(position: number): number | null {
  return AUTO_COMPACT_PERCENTS[position] ?? null;
}

/** The popover heading for a slider position. */
export function autoCompactHeading(position: number): string {
  const pct = autoCompactValue(position);
  return pct === null ? 'Compact at default' : `Compact at ${pct}%`;
}

/**
 * Whether the running terminal still carries an older threshold than the saved
 * one. Only while a terminal is running; a started-with record from before the
 * field existed (undefined) counts as Default (null).
 */
export function autoCompactRestartOwed(
  startedWith: { autoCompactPct?: number | null } | null,
  savedPct: number | null | undefined,
): boolean {
  if (startedWith === null) return false;
  return (startedWith.autoCompactPct ?? null) !== (savedPct ?? null);
}

/** What the popover shows for a given state of the status read: one decision, so "disabled" and "explained" cannot drift. */
export interface AutoCompactPanelState {
  /** The slider cannot be moved. True iff `notice` is set. */
  sliderDisabled: boolean;
  /** The one line that says why the slider is unavailable; null when it is usable. */
  notice: 'loading' | 'error' | null;
  /** The restart row: only with data to compare against, and only while a restart is owed. */
  showRestartRow: boolean;
}

/**
 * Without status data nothing is owed and nothing can be saved: the saved value
 * is unknown, so a restart row would be a guess and a slider position a lie.
 */
export function autoCompactPanelState(input: {
  hasData: boolean;
  isError: boolean;
  startedWith: { autoCompactPct?: number | null } | null;
  saved: number | null | undefined;
}): AutoCompactPanelState {
  const { hasData, isError, startedWith, saved } = input;
  if (!hasData) {
    return { sliderDisabled: true, notice: isError ? 'error' : 'loading', showRestartRow: false };
  }
  return { sliderDisabled: false, notice: null, showRestartRow: autoCompactRestartOwed(startedWith, saved) };
}
